import api from '../../../services/api'
import type { BruktbilFilters } from '../useBruktbilFilters'

export interface KpiCardsData {
  total_transfers: number
  unique_sellers: number
  unique_buyers: number
}

export interface WeeklyPoint {
  week: string
  count: number
  uke_label?: string | null
  klasse?: string | null
}

export interface ClassShare {
  klasse: string
  count: number
  share: number
}

export interface OversiktResponse {
  kpis: KpiCardsData
  weekly_trend: WeeklyPoint[]
  class_share: ClassShare[]
}

// Only the global filter fields are needed — narrow so callers don't have to
// supply the module-local merke/modell/aar state from useBruktbilFilters.
type GeoParams = { selgerPostnummer?: string | null; kjoperPostnummer?: string | null }
type GlobalParams = Pick<BruktbilFilters, 'from' | 'to' | 'klasse' | 'varebil' | 'omregistrering'> & GeoParams

function geoParams(f: GeoParams) {
  return {
    ...(f.selgerPostnummer ? { selger_postnummer: f.selgerPostnummer.split(',') } : {}),
    ...(f.kjoperPostnummer ? { kjoper_postnummer: f.kjoperPostnummer.split(',') } : {}),
  }
}

// --- Markedspuls (#928) -----------------------------------------------------

export type PulsKortId = 'i_gar' | 'uke' | 'maned' | 'hittil_i_ar'
export type PulsMot = 'forrige_uke' | 'forrige_maned' | 'i_fjor'

export interface PulsSammenligning {
  mot: PulsMot
  fra: string
  til: string
  /** null when the window predates the data history */
  antall: number | null
  endring: number | null
  /** null when antall is null or 0 */
  endring_pct: number | null
  mangler_data: boolean
}

export interface PulsKort {
  id: PulsKortId
  fra: string
  til: string
  antall: number
  sammenligninger: PulsSammenligning[]
}

export interface PulsResponse {
  data_through: string | null
  kort: PulsKort[]
}

export type Granularity = 'day' | 'week' | 'month'

export interface PeriodeBucket {
  index: number
  label: string
  period_start: string
  period_end: string
  is_partial: boolean
  before_history: boolean
  total: number
  klasser: Record<string, number>
}

export interface PeriodeSeries {
  start_date: string
  end_date: string
  buckets: PeriodeBucket[]
}

export interface PeriodeSerieResponse {
  granularity: Granularity
  klasser: string[]
  current: PeriodeSeries
  comparison: PeriodeSeries | null
  data_through: string
}

export interface TopplisteRad {
  merke: string | null
  /** null on the merke list */
  modell: string | null
  antall: number
  /** the comparison fields are null without a comparison */
  antall_forrige: number | null
  endring: number | null
  /** also null when antall_forrige is 0 */
  endring_pct: number | null
  andel_pct: number
}

export interface TopplistePlass extends TopplisteRad {
  rang: number
}

export interface TopplisteResponse {
  data_through: string
  fra: string
  til: string
  prev_fra: string | null
  prev_til: string | null
  total: number
  comparison_unavailable: 'before_history' | null
  modeller: TopplistePlass[]
  merker: TopplistePlass[]
  /** models only; null without a comparison */
  bevegelser: { opp: TopplisteRad[]; ned: TopplisteRad[] } | null
}

export function fetchToppliste(
  filters: GlobalParams,
  prev: { from: string; to: string },
): Promise<{ data: TopplisteResponse }> {
  return api.get('/bruktbil/oversikt/toppliste', {
    params: {
      from_date: filters.from,
      to_date: filters.to,
      prev_from_date: prev.from,
      prev_to_date: prev.to,
      klasse: filters.klasse,
      varebil: filters.varebil,
      omregistrering: filters.omregistrering || undefined,
      ...geoParams(filters),
    },
  })
}

/** The pulse ignores the period and the omregistrering toggle by design. */
export function fetchPuls(filters: Pick<GlobalParams, 'klasse' | 'varebil'> & GeoParams): Promise<{ data: PulsResponse }> {
  return api.get('/bruktbil/oversikt/puls', {
    params: { klasse: filters.klasse, varebil: filters.varebil, ...geoParams(filters) },
  })
}

export function fetchPeriodeSerie(
  filters: GlobalParams,
  granularity: Granularity,
  prev: { from: string; to: string } | null,
): Promise<{ data: PeriodeSerieResponse }> {
  return api.get('/bruktbil/oversikt/periode-serie', {
    params: {
      from_date: filters.from,
      to_date: filters.to,
      granularity,
      ...(prev ? { prev_from_date: prev.from, prev_to_date: prev.to } : {}),
      klasse: filters.klasse,
      varebil: filters.varebil,
      omregistrering: filters.omregistrering || undefined,
      ...geoParams(filters),
    },
  })
}

export function fetchOversikt(filters: GlobalParams): Promise<{ data: OversiktResponse }> {
  return api.get('/bruktbil/oversikt', {
    params: {
      from_date: filters.from,
      to_date: filters.to,
      klasse: filters.klasse,
      varebil: filters.varebil,
      omregistrering: filters.omregistrering || undefined,
      ...geoParams(filters),
    },
  })
}
